import React, { useEffect, useContext, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { TitleSmall400, TitleSmall500, TitleXSmall400, TitleXSmall500 } from '@nebula/global-styles';
import { useMediaQuery } from '@nebula/hooks';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { ThemeContext } from 'styled-components';
import { ErrorAlert, SuccessAlert, WarningAlert } from '@nebula/alert';
import { ApplePay, GooglePay } from '@epl/icons';
import {
  ChangePaymentMethodButton,
  Entry,
  InformationCard,
  LargePaymentInfoTitle,
  PaymentInfo,
  PaymentMethodDetails,
  StandardPaymentInfoTitle,
} from './index.styles';
import { familyName, splitsList } from '../../../constants';
import useExistingPaymentMethod from '../../PaymentCaptureMethod/hooks/useExistingPaymentMethod';
import { SmallCardIcon } from '../../PaymentCaptureMethod/PaymentCardForm/Icons';
import { PAYMENT_STAGES, PaymentContext } from '../../PaymentOrchestrator/PaymentContextProvider';
import { Alert } from '../index.styles';
import { trackPaymentMethodChangeStarted } from '../../../helpers/switch/analytics';
import { PaymentMethodTypes } from '../../PaymentCaptureMethod/constants.ts';
import usePaymentMethods from '../../PaymentOrchestrator/hooks/usePaymentMethods';
import InViewComponent from '../../InView';
import PaymentMethodChange from '../../../components/PaymentMethodChange';
import useSplits from '../../../hooks/useSplits';

dayjs.extend(advancedFormat);

const InformationRow = ({ label, value, testId, mask }) => {
  const theme = useContext(ThemeContext);
  const useLargerCopy = useMediaQuery(theme.media.tabletPortraitMin);

  const EntryTitle = useLargerCopy ? TitleSmall500 : TitleXSmall500;
  const EntryValue = useLargerCopy ? TitleSmall400 : TitleXSmall400;

  return (
    <Entry data-hook={mask ? 'fs-exclude-element' : undefined}>
      <EntryTitle>{label}:</EntryTitle> <EntryValue data-automation-test-element={testId}>{value}</EntryValue>
    </Entry>
  );
};

const onSuccessAlertRef = node => {
  if (node) {
    node.scrollIntoView({ behavior: 'smooth' });
  }
};

const ExistingPaymentDetails = () => {
  const currentSubscription = useSelector(state => state.subscriptions?.current?.family);

  const { existingPaymentType, existingPaymentDetails } = useExistingPaymentMethod();

  const { paymentStage, paymentChangeActive, setPaymentChangeActive } = useContext(PaymentContext);
  const theme = useContext(ThemeContext);
  const useLargerCopy = useMediaQuery(theme.media.tabletPortraitMin);
  const PaymentInformationTitle = useLargerCopy ? LargePaymentInfoTitle : StandardPaymentInfoTitle;
  const availablePaymentMethods = usePaymentMethods();
  const cardSectionRef = useRef(null);

  const onTriggerChangeMethod = () => {
    trackPaymentMethodChangeStarted(availablePaymentMethods, existingPaymentType);
    setPaymentChangeActive(!paymentChangeActive);
  };

  const nextBillingDate = useSelector(state => state.billing?.nextBillingDate);
  const billingPrice = useSelector(state => state.billing?.signupPrice);
  const monthlyBilling = useSelector(state => state.billing?.billingFrequency === 'Monthly');
  const isInRecycle = useSelector(state => state?.billing?.billingStateRecycle);
  const billingDay = dayjs(nextBillingDate).format('Do');
  const nextBillDateLong = dayjs(nextBillingDate).format('Do MMMM YYYY');
  const paymentStageUpdated = paymentStage === PAYMENT_STAGES.PAYMENT_METHOD_UPDATED;

  const splits = useSplits([splitsList.billingDeclineIncidentJuly25]);
  const billingDeclineIncidentJuly25 = splits[splitsList.billingDeclineIncidentJuly25];

  useEffect(() => {
    if (billingDeclineIncidentJuly25 && cardSectionRef.current) {
      cardSectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [billingDeclineIncidentJuly25]);

  return (
    <PaymentInfo>
      <PaymentInformationTitle>Your payment information</PaymentInformationTitle>
      <InformationCard>
        {currentSubscription && (
          <InformationRow label="Subscription" value={familyName[currentSubscription]} testId="YourSubscription" />
        )}
        {!!billingPrice && <InformationRow label="Price" value={`£${billingPrice}`} testId="YourPrice" />}
        {nextBillingDate && monthlyBilling && billingDay && (
          <InformationRow label="Billing date" value={`${billingDay} of each month`} testId="YourBillingDate" />
        )}
        {nextBillingDate && nextBillDateLong && (
          <InformationRow label="Next bill" value={nextBillDateLong} testId="YourNextBill" />
        )}
      </InformationCard>
      {existingPaymentType && (
        <PaymentMethodDetails>
          {paymentStage === PAYMENT_STAGES.PAYMENT_METHOD_UPDATED && (
            <Alert ref={onSuccessAlertRef} fullWidth>
              <SuccessAlert
                title={
                  billingDeclineIncidentJuly25 && nextBillDateLong
                    ? `Payment method updated. Your next bill is due on ${nextBillDateLong}.`
                    : 'Payment method updated'
                }
                message=""
                showIcon
              />
            </Alert>
          )}
          {billingDeclineIncidentJuly25 && !paymentStageUpdated && (
            <div ref={cardSectionRef}>
              <Alert>
                <WarningAlert
                  title={
                    existingPaymentType === PaymentMethodTypes.Card
                      ? 'Please re-add your payment method'
                      : 'Payment issue detected'
                  }
                  message={
                    existingPaymentType === PaymentMethodTypes.Card
                      ? 'We detected an issue with your details during your last payment.'
                      : 'Please add your payment details again or choose a different method.'
                  }
                  showIcon
                />
              </Alert>
            </div>
          )}
          {isInRecycle && (
            <InViewComponent label="recycle_payment_method_alert" multi>
              <Alert>
                <ErrorAlert title="Your payment method has expired" message="" showIcon />
              </Alert>
            </InViewComponent>
          )}
          {existingPaymentType === PaymentMethodTypes.Card && (
            <React.Fragment>
              <InformationRow
                label="Card type"
                value={<SmallCardIcon creditCardType={existingPaymentDetails.creditCardType} />}
                testId="CardPaymentMethod"
              />
              <InformationRow label="Card ending" value={existingPaymentDetails.cardNumber} mask />
              <InformationRow label="Expires" value={existingPaymentDetails.cardExpiryDateLong} mask />
            </React.Fragment>
          )}
          {existingPaymentType === PaymentMethodTypes.ApplePay && (
            <InformationRow label="Payment method" value={<ApplePay />} testId="ApplePayPaymentMethod" />
          )}
          {existingPaymentType === PaymentMethodTypes.GooglePay && (
            <InformationRow label="Payment method" value={<GooglePay />} testId="GooglePayPaymentMethod" />
          )}
          <ChangePaymentMethodButton
            type="button"
            label="Change payment method"
            buttonKind="secondaryBlue"
            disabled={paymentChangeActive || (billingDeclineIncidentJuly25 && !paymentStageUpdated)}
            onClick={onTriggerChangeMethod}
            data-automation-test-element="change-payment-method"
          />
        </PaymentMethodDetails>
      )}
      {billingDeclineIncidentJuly25 && !paymentStageUpdated && <PaymentMethodChange forceShow />}
    </PaymentInfo>
  );
};

InformationRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  testId: PropTypes.string,
  mask: PropTypes.bool,
};

InformationRow.defaultProps = {
  testId: '',
};

export default ExistingPaymentDetails;
